import { AppWindow, BarChart3, Layers, Loader2, Network, Power, PowerOff, Server, TriangleAlert, UsersRound } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageLayout } from '../../components/Layout/PageLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/Common/tabs';
import { Button } from '../../components/Common/button';
import {
  ApplicationsTab,
  ClientsTab,
  DiagramTab,
  ForgesTab,
  OverviewTab,
} from '../../components/ForgeDashboard';
import forgeDashboardService, { ForgeDashboardData } from '../../services/forgeDashboardService';
import { useIsSuperAdmin } from '../../stores/authStore';
import { useForgeApiEnabled, useForgeApiStore } from '../../stores/forgeApiStore';

const VALID_TABS = ['overview', 'forge-clients', 'forges', 'applications', 'diagram'] as const;
type TabId = (typeof VALID_TABS)[number];

const isValidTab = (v: string | null): v is TabId =>
  !!v && (VALID_TABS as readonly string[]).includes(v);

const ForgeDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSuperAdmin = useIsSuperAdmin();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab: TabId = isValidTab(requestedTab) ? requestedTab : 'overview';

  const [dashboardData, setDashboardData] = useState<ForgeDashboardData | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  // null until the switch state is known, so no Forge request fires before we know it is allowed.
  const apiEnabled = useForgeApiEnabled();
  const setApiEnabled = useForgeApiStore((s) => s.setEnabled);
  const [apiSwitchSaving, setApiSwitchSaving] = useState(false);
  const [apiSwitchError, setApiSwitchError] = useState<string | null>(null);

  const loadOverview = async () => {
    if (!isSuperAdmin) {
      setOverviewError(t('forgeDashboard.error.unauthorized', 'You do not have permission to view this page.'));
      setOverviewLoading(false);
      return;
    }
    try {
      setOverviewLoading(true);
      setOverviewError(null);
      const data = await forgeDashboardService.getAllDashboardData();
      setDashboardData(data);
      setLastRefresh(new Date());
    } catch (err: unknown) {
      console.error('Failed to load forge dashboard data:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setOverviewError(t('forgeDashboard.error.loadFailed', 'Failed to load dashboard data: {{error}}', { error: msg }));
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (apiEnabled && activeTab === 'overview') {
      loadOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, activeTab, apiEnabled]);

  const toggleApiSwitch = async () => {
    if (apiEnabled === null) return;
    try {
      setApiSwitchSaving(true);
      setApiSwitchError(null);
      const { enabled } = await forgeDashboardService.setApiSwitch(!apiEnabled);
      setApiEnabled(enabled);
      if (!enabled) setDashboardData(null);
    } catch (err: unknown) {
      console.error('Failed to update Forge API switch:', err);
      setApiSwitchError(t('forgeDashboard.apiSwitch.updateFailed', 'Failed to update the Forge API switch'));
    } finally {
      setApiSwitchSaving(false);
    }
  };

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    setSearchParams(next, { replace: true });
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <TriangleAlert className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {t('forgeDashboard.error.accessDenied', 'Access Denied')}
          </h2>
          <p className="text-muted-foreground mb-4">
            {t('forgeDashboard.error.unauthorized', 'You do not have permission to view this page.')}
          </p>
          <Button onClick={() => navigate('/welcome')}>
            {t('global.backToDashboard', 'Back to Dashboard')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      icon={<Server className="h-6 w-6" />}
      title={t('forgeDashboard.title', 'Forge Dashboard')}
      subtitle={t('forgeDashboard.subtitle', 'System health and super-admin management')}
      headerRight={
        <div className="flex items-center gap-3">
          {apiSwitchError && <span className="text-sm text-destructive">{apiSwitchError}</span>}
          <span className="text-sm text-muted-foreground">
            {t('forgeDashboard.apiSwitch.label', 'Forge API')}
          </span>
          <Button
            variant={apiEnabled === false ? 'destructive' : 'default'}
            size="sm"
            className="gap-2 min-w-[5rem]"
            onClick={toggleApiSwitch}
            disabled={apiEnabled === null || apiSwitchSaving}
            aria-pressed={apiEnabled === true}
            title={
              apiEnabled === false
                ? t('forgeDashboard.apiSwitch.turnOn', 'Turn calls to the Forge API back on')
                : t('forgeDashboard.apiSwitch.turnOff', 'Turn off all calls to the Forge API')
            }
          >
            {apiSwitchSaving || apiEnabled === null ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : apiEnabled ? (
              <Power className="h-4 w-4" />
            ) : (
              <PowerOff className="h-4 w-4" />
            )}
            {apiEnabled === false
              ? t('forgeDashboard.apiSwitch.off', 'Off')
              : t('forgeDashboard.apiSwitch.on', 'On')}
          </Button>
        </div>
      }
    >
      {apiEnabled === null ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !apiEnabled ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <PowerOff className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {t('forgeDashboard.apiSwitch.disabledTitle', 'Forge API calls are turned off')}
            </h2>
            <p className="text-muted-foreground">
              {t(
                'forgeDashboard.apiSwitch.disabledDescription',
                'Etabli is not sending any request to the Forge API. Turn it back on to load Forge data.',
              )}
            </p>
          </div>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={handleTabChange} defaultValue="overview">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              {t('forgeDashboard.tabs.overview', 'Overview')}
            </TabsTrigger>
            <TabsTrigger value="forge-clients" className="gap-2">
              <UsersRound className="h-4 w-4" />
              {t('forgeDashboard.tabs.clients', 'Forge clients')}
            </TabsTrigger>
            <TabsTrigger value="forges" className="gap-2">
              <Layers className="h-4 w-4" />
              {t('forgeDashboard.tabs.forges', 'Forges')}
            </TabsTrigger>
            <TabsTrigger value="applications" className="gap-2">
              <AppWindow className="h-4 w-4" />
              {t('forgeDashboard.tabs.applications', 'Applications')}
            </TabsTrigger>
            <TabsTrigger value="diagram" className="gap-2">
              <Network className="h-4 w-4" />
              {t('forgeDashboard.tabs.diagram', 'Diagram')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {overviewError ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <TriangleAlert className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    {t('forgeDashboard.error.title', 'Error Loading Dashboard')}
                  </h2>
                  <p className="text-muted-foreground mb-4">{overviewError}</p>
                  <Button onClick={loadOverview}>{t('global.retry', 'Retry')}</Button>
                </div>
              </div>
            ) : (
              <OverviewTab
                dashboardData={dashboardData}
                loading={overviewLoading}
                lastRefresh={lastRefresh}
                onRefresh={loadOverview}
              />
            )}
          </TabsContent>

          <TabsContent value="forge-clients">
            <ClientsTab />
          </TabsContent>

          <TabsContent value="forges">
            <ForgesTab />
          </TabsContent>

          <TabsContent value="applications">
            <ApplicationsTab />
          </TabsContent>

          <TabsContent value="diagram">
            <DiagramTab />
          </TabsContent>
        </Tabs>
      )}
    </PageLayout>
  );
};

export default ForgeDashboardPage;
